/**
 * OP-UX-02 / #1114 — Expand/collapse state for Opportunity subsection cards.
 * In-memory cache + durable storage (localStorage web / AsyncStorage native) per request + section.
 */

import { storage } from '../services/storage';

const memoryStore = new Map<string, boolean>();
const STORAGE_PREFIX = 'opp-expand:';

function storageKey(requestId: string | undefined, sectionId: string): string {
  return `${STORAGE_PREFIX}${requestId ?? 'create'}:${sectionId}`;
}

function memoryKey(requestId: string | undefined, sectionId: string): string {
  return `${requestId ?? 'create'}:${sectionId}`;
}

function isCreateFlowRequest(requestId: string | undefined): boolean {
  return requestId == null || requestId === '';
}

export function readOpportunitySubsectionExpanded(
  requestId: string | undefined,
  sectionId: string,
  defaultExpanded: boolean,
): boolean {
  const key = memoryKey(requestId, sectionId);
  const stored = memoryStore.get(key);
  return stored ?? defaultExpanded;
}

export async function hydrateOpportunitySubsectionExpanded(
  requestId: string | undefined,
  sectionId: string,
  defaultExpanded: boolean,
): Promise<boolean> {
  const memKey = memoryKey(requestId, sectionId);
  if (memoryStore.has(memKey)) {
    return memoryStore.get(memKey) ?? defaultExpanded;
  }
  if (!isCreateFlowRequest(requestId)) {
    const raw = await storage.getItem(storageKey(requestId, sectionId));
    if (raw === 'true' || raw === 'false') {
      const value = raw === 'true';
      memoryStore.set(memKey, value);
      return value;
    }
  }
  return defaultExpanded;
}

export function writeOpportunitySubsectionExpanded(
  requestId: string | undefined,
  sectionId: string,
  expanded: boolean,
): void {
  const memKey = memoryKey(requestId, sectionId);
  memoryStore.set(memKey, expanded);
  if (!isCreateFlowRequest(requestId)) {
    void storage.setItem(storageKey(requestId, sectionId), expanded ? 'true' : 'false');
  }
}

export function clearOpportunitySubsectionExpandState(requestId: string): void {
  const prefix = `${requestId}:`;
  for (const key of memoryStore.keys()) {
    if (key.startsWith(prefix)) {
      memoryStore.delete(key);
    }
  }
}

/** OP-UX-01 — fresh Create Request: only the first card should start expanded. */
export function clearOpportunitySubsectionExpandStateForCreate(): void {
  clearOpportunitySubsectionExpandState('create');
}

/** Test-only */
export function resetOpportunitySubsectionExpandStateForTests(): void {
  memoryStore.clear();
}
